import React, { useState, useMemo } from "react";
import { useFinance } from "../context/FinanceContext";
import {
  PieChart, Pie, Cell, Tooltip, Legend, ResponsiveContainer,
  BarChart, Bar, XAxis, YAxis, CartesianGrid,
} from "recharts";
import "./ChartPanel.css";

const SIMPLE_COLORS = ["#2e7d52", "#ef4444", "#8b1a1a"];
const ALL_COLORS = ["#1a5c38", "#22c55e", "#1a3a6e", "#ef4444", "#8b1a1a"];

// ── Helpers ────────────────────────────────────────────────────────────────
function fmt(n) {
  return "₹" + Number(n).toLocaleString("en-IN");
}

function filterByRange(items, dateKey, months) {
  if (!months) return items; // "all"
  const cutoff = new Date();
  cutoff.setMonth(cutoff.getMonth() - months);
  return items.filter((i) => new Date(i[dateKey]) >= cutoff);
}

// ── Custom tooltips ────────────────────────────────────────────────────────
const PieTooltip = ({ active, payload }) => {
  if (active && payload && payload.length) {
    return (
      <div className="chart-tooltip">
        <p className="chart-tooltip__label">{payload[0].name}</p>
        <p className="chart-tooltip__item" style={{ color: payload[0].payload.fill }}>
          {fmt(payload[0].value)}
        </p>
      </div>
    );
  }
  return null;
};

const BarTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="chart-tooltip">
        <p className="chart-tooltip__label">{label}</p>
        {payload.map((p, i) => (
          <p key={i} style={{ color: p.color }} className="chart-tooltip__item">
            {p.name}: {fmt(p.value)}
          </p>
        ))}
      </div>
    );
  }
  return null;
};


export default function ChartPanel() {
  const { derived, state } = useFinance();

  // view mode: simple / complex
  const [mode, setMode] = useState("simple");

  const filteredIncomes = state.incomes;
  const filteredCompleted = state.completedExpenses;
  const filteredTotalIncome  = filteredIncomes.reduce((s, i) => s + i.amount, 0);
  const filteredTotalExpense = filteredCompleted.reduce((s, e) => s + e.amount, 0);
  const filteredAllocated    = filteredIncomes.reduce(
    (s, i) => s + (i.allocated || []).reduce((a, al) => a + al.amount, 0), 0
  );
  const filteredGenerating   = filteredTotalIncome - filteredAllocated;

  // ── Pie chart data ───────────────────────────────────────────────────────
  const simpleData = [
  { name: "Generating", value: filteredGenerating,   fill: "#2e7d52" },
  { name: "Completed",  value: filteredTotalExpense, fill: "#ef4444" },
  { name: "Awaiting",   value: derived.totalAwaiting, fill: "#8b1a1a" },
];

  const complexData = [
    { name: "Reserve",    value: derived.totalReserve,  fill: "#1a5c38" },
    { name: "Generating", value: filteredGenerating,    fill: "#22c55e" },
    { name: "Income",     value: filteredTotalIncome,   fill: "#1a3a6e" },
    { name: "Completed",  value: filteredTotalExpense,  fill: "#ef4444" },
    { name: "Awaiting",   value: derived.totalAwaiting, fill: "#8b1a1a" },
  ];

  const pieData   = mode === "simple" ? simpleData : complexData;
  const pieColors = mode === "simple" ? SIMPLE_COLORS : ALL_COLORS;

  // ── Income vs Expense comparison bar data ────────────────────────────────
  // Group completed expenses and incomes by category
  const categoryNames = state.reserveCategories.map((c) => c.name);

  const compareData = categoryNames.map((cat) => {
    const income  = filteredIncomes.filter((i) => i.office === cat).reduce((s, i) => s + i.amount, 0);
    const expense = filteredCompleted.filter((e) => e.category === cat).reduce((s, e) => s + e.amount, 0);
    return { name: cat, Income: income, Expense: expense };
  });

  // ── Render ───────────────────────────────────────────────────────────────
  return (
    <div className="chart-panel">
      {/* Header */}
      <div className="chart-panel__header">
        <div className="chart-panel__title">
          <i className="fa-solid fa-chart-pie"></i>
          Financial Overview
        </div>
        <div className="mode-toggle-wrap">
  <span className={mode === "simple" ? "mode-label active" : "mode-label"}>
    Simple
  </span>

  <button
    className={`ios-toggle ${mode === "complex" ? "active" : ""}`}
    onClick={() =>
      setMode(mode === "simple" ? "complex" : "simple")
    }
  >
    <div className="ios-toggle-knob"></div>
  </button>

  <span className={mode === "complex" ? "mode-label active" : "mode-label"}>
    Complex
  </span>
</div>
      </div>

      {/* ── Pie chart ── */}
      <div className="chart-section">
        <div className="chart-section-label">
          <i className="fa-solid fa-circle-nodes"></i>
          {mode === "simple" ? "Core Metrics" : "All Metrics"}
        </div>
        <ResponsiveContainer width="100%" height={200}>
          <PieChart>
            <Pie
  data={pieData}
  cx="50%"
  cy="50%"
  innerRadius={50}
  outerRadius={80}
  paddingAngle={3}
  dataKey="value"
  minAngle={8}
>
              {pieData.map((entry, i) => (
                <Cell key={i} fill={entry.fill || pieColors[i % pieColors.length]} />
              ))}
            </Pie>
            <Tooltip content={<PieTooltip />} />
            <Legend
              iconType="circle"
              iconSize={8}
              wrapperStyle={{ fontSize: "11px" }}
            />
          </PieChart>
        </ResponsiveContainer>
      </div>

    </div>
  );
}